import React, { useState } from 'react';
import { LifeBuoy, CheckCircle, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { createSupportTicket } from '../api';
import SupportChatBot from './SupportChatBot';

const SupportPage = ({ userEmail }) => {
    const [formData, setFormData] = useState({
        subject: '',
        category: 'bug',
        priority: 'medium',
        description: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await createSupportTicket({
                ...formData,
                email: userEmail,
                timestamp: new Date().toISOString()
            });
            setIsSuccess(true);
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center dark:bg-slate-950 bg-slate-50">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-emerald-500/10 p-6 rounded-full text-emerald-400 mb-6"
                >
                    <CheckCircle size={48} />
                </motion.div>
                <h2 className="text-3xl font-bold dark:text-white text-slate-900 mb-2">Ticket Submitted!</h2>
                <p className="text-slate-400 max-w-md mb-8">
                    Thank you for your report. Our support team has been notified and will reach out to you at <strong>{userEmail}</strong> shortly.
                </p>
                <button
                    onClick={() => { setIsSuccess(false); setFormData({ subject: '', category: 'bug', priority: 'medium', description: '' }); }}
                    className="px-6 py-2 dark:bg-slate-800 bg-white hover:bg-slate-50 dark:hover:bg-slate-700 dark:text-white text-slate-700 rounded-lg transition-colors border dark:border-slate-700 border-slate-300 dark:hover:border-slate-500"
                >
                    Submit Another Ticket
                </button>
            </div>
        );
    }

    return (
        <div className="h-full dark:bg-slate-950 bg-slate-50 overflow-y-auto p-4 md:p-8 relative">
            <SupportChatBot />
            <div className="max-w-3xl mx-auto">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-pink-500/10 rounded-lg text-pink-400 border border-pink-500/20">
                            <LifeBuoy size={24} />
                        </div>
                        <h1 className="text-2xl font-bold dark:text-white text-slate-900">Support Center</h1>
                    </div>
                    <p className="dark:text-slate-400 text-slate-500">
                        Encountered an issue? Let us know and we'll fix it right away.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6 md:p-8 space-y-6 shadow-xl">
                    {/* Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700">Category</label>
                            <select
                                value={formData.category}
                                onChange={e => setFormData({ ...formData, category: e.target.value })}
                                className="w-full dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-300 rounded-lg px-4 py-2.5 dark:text-slate-200 text-slate-900 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 outline-none transition-all"
                            >
                                <option value="bug">Report a Bug</option>
                                <option value="feature">Feature Request</option>
                                <option value="billing">Billing Issue</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700">Priority</label>
                            <select
                                value={formData.priority}
                                onChange={e => setFormData({ ...formData, priority: e.target.value })}
                                className="w-full dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-300 rounded-lg px-4 py-2.5 dark:text-slate-200 text-slate-900 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 outline-none transition-all"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium dark:text-slate-300 text-slate-700">Subject</label>
                        <input
                            type="text"
                            required
                            value={formData.subject}
                            onChange={e => setFormData({ ...formData, subject: e.target.value })}
                            placeholder="Brief summary of the issue..."
                            className="w-full dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-300 rounded-lg px-4 py-2.5 dark:text-slate-200 text-slate-900 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 outline-none transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium dark:text-slate-300 text-slate-700">Description</label>
                        <textarea
                            required
                            rows={6}
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Please provide details about what happened..."
                            className="w-full dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-300 rounded-lg px-4 py-2.5 dark:text-slate-200 text-slate-900 focus:ring-1 focus:ring-sky-500 focus:border-sky-500 outline-none resize-none transition-all"
                        />
                    </div>

                    <div className="pt-4 flex justify-end">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-8 py-3 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-sky-900/20 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? "Submitting..." : (
                                <>
                                    Submit Ticket <Send size={18} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
export default SupportPage;
